import { useState } from "react";
import { Star, MessageSquarePlus, Loader2 } from "lucide-react";
import { addRouteReview } from "@/lib/api";
import { toast } from "sonner";

export default function RouteReviews({ slug, reviews = [], summary, onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", title: "", rating: 5, comment: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.comment) return toast.error("Preencha nome e comentário");
    setLoading(true);
    try {
      const res = await addRouteReview(slug, { name: f.name, title: f.title, rating: Number(f.rating), comment: f.comment });
      toast.success("Avaliação enviada. Obrigado!");
      setF({ name: "", title: "", rating: 5, comment: "" });
      setOpen(false);
      onAdded?.(res);
    } catch {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally { setLoading(false); }
  };

  return (
    <section className="section pt-0" data-testid="route-reviews">
      <div className="container-rotta">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="flex items-center gap-3 text-[#C6A762] mb-4"><span className="gold-divider" /><span className="text-[11px] tracking-[0.35em] uppercase">Avaliações</span></div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">O que a comunidade diz.</h2>
          </div>
          <div className="flex items-center gap-5">
            {summary?.count > 0 && (
              <div className="flex items-center gap-3 text-[#C6A762]">
                <Star size={22} fill="#C6A762" />
                <span className="font-display text-3xl text-white">{summary.average}</span>
                <span className="text-[#B8B8B8] text-sm">({summary.count})</span>
              </div>
            )}
            <button onClick={() => setOpen((o) => !o)} data-testid="review-open-form" className="btn-gold px-5 py-3 rounded-full text-xs inline-flex items-center gap-2">
              <MessageSquarePlus size={14} /> Avaliar rota
            </button>
          </div>
        </div>

        {open && (
          <form onSubmit={submit} className="card-rotta p-7 bg-[#141414] mb-10 grid md:grid-cols-2 gap-4" data-testid="review-form">
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#C6A762] block mb-2">Seu nome</label>
              <input data-testid="review-name" className="input-rotta" value={f.name} onChange={set("name")} placeholder="Como quer aparecer" required />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#C6A762] block mb-2">Título</label>
              <input data-testid="review-title" className="input-rotta" value={f.title} onChange={set("title")} placeholder="Ex: Excelente experiência" />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#C6A762] block mb-2">Nota</label>
              <div className="flex items-center gap-2" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} data-testid={`review-rating-star-${n}`} onClick={() => setF((s) => ({ ...s, rating: n }))} className="p-1">
                    <Star size={22} className={n <= f.rating ? "text-[#C6A762]" : "text-[#3a3a3a]"} fill={n <= f.rating ? "#C6A762" : "none"} />
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#C6A762] block mb-2">Comentário</label>
              <textarea data-testid="review-comment" rows={3} className="input-rotta" value={f.comment} onChange={set("comment")} placeholder="Percurso incrível. Estradas excelentes e ótimos pontos de parada." required />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button disabled={loading} data-testid="review-submit" className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 size={14} className="animate-spin" /> : null} Enviar avaliação
              </button>
            </div>
          </form>
        )}

        {reviews.length === 0 ? (
          <div className="card-rotta p-10 bg-[#141414] text-center text-[#B8B8B8]" data-testid="reviews-empty">
            Ainda não há avaliações desta rota. Seja o primeiro a compartilhar sua experiência!
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {reviews.slice().reverse().map((r, i) => (
              <article key={i} className="card-rotta p-6" data-testid={`review-${i}`}>
                <div className="flex items-center gap-1 text-[#C6A762] mb-3">
                  {Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={13} fill="#C6A762" />)}
                </div>
                {r.title && <div className="font-display text-lg text-white mb-1">{r.title}</div>}
                <p className="text-[#E8E8E8] text-sm leading-relaxed">&ldquo;{r.comment}&rdquo;</p>
                <div className="mt-5 pt-4 border-t border-[#2a2a2a] flex items-center justify-between text-[11px] tracking-[0.2em] uppercase">
                  <span className="text-white font-medium">{r.name}</span>
                  <span className="text-[#7d7d7d]">{r.date ? new Date(r.date + "T12:00:00").toLocaleDateString("pt-BR", { month: "short", year: "numeric" }) : ""}</span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
